import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "./FichaPrevia.css";
import "./AgendaPublica.css";
import logoSalvir from "../../assets/Logo_SALVIR.png";
import logoSalvirLetras from "../../assets/Logo_SALVIR_letras.png";
import { getAgendaPublica, getHorariosPublicos, agendarPublico } from "../../services/citas";
import FormularioDatosPaciente, {
  validarDatosPaciente,
  armarPayloadPaciente,
} from "./FormularioDatosPaciente";
import { toast } from "react-toastify";

/**
 * Página pública de agendamiento (SIN login).
 * URL: /agendar/:bookingId
 *
 * 1) Elegir día y hora (calendario del mes + semana con horas libres)
 * 2) Llenar datos (mismo formulario de la ficha previa)
 * 3) Listo
 */

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Date.getDay(): 0 = domingo
const DIAS_JS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const DIAS_CORTOS = ["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"];
const LETRAS_SEMANA = ["D", "L", "M", "X", "J", "V", "S"];
const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// "2026-10-15" -> Date local (sin problemas de zona horaria)
const aFecha = (texto) => {
  const [a, m, d] = String(texto).split("-").map(Number);
  return new Date(a, m - 1, d);
};

// Date -> "2026-10-15"
const aTexto = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`;

const sumarDias = (fecha, n) => {
  const d = new Date(fecha);
  d.setDate(d.getDate() + n);
  return d;
};

const mismoDia = (a, b) => aTexto(a) === aTexto(b);

const primeroDelMes = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), 1);

const capitalizar = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1);

const formatoLargo = (texto) =>
  capitalizar(
    aFecha(texto).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
  );

// "14:30" -> "2:30 pm"
const formatoHora = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

// Cuántos días se ven a la vez según el ancho de pantalla
const calcularDiasPorVista = () => {
  if (window.innerWidth <= 640) return 1;
  if (window.innerWidth <= 1024) return 4;
  return 7;
};

const iniciales = (nombre) =>
  String(nombre || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

const AgendaPublica = () => {
  const { bookingId } = useParams();

  const [cargando, setCargando] = useState(true);
  const [agenda, setAgenda] = useState(null);
  const [errorGeneral, setErrorGeneral] = useState("");

  // Paso: "fecha" -> "datos" -> "listo"
  const [paso, setPaso] = useState("fecha");
  const [fechaElegida, setFechaElegida] = useState("");
  const [horaElegida, setHoraElegida] = useState("");
  const [avisoHora, setAvisoHora] = useState("");

  // Calendario
  const [inicioVentana, setInicioVentana] = useState(null); // primer día visible de la semana
  const [mesVisible, setMesVisible] = useState(null);       // mes del calendario chiquito
  const [diasPorVista, setDiasPorVista] = useState(calcularDiasPorVista());
  const [horariosPorDia, setHorariosPorDia] = useState({}); // { "2026-10-20": ["10:30", ...] }

  // Formulario (se conserva aunque regrese a elegir otra hora)
  const [datos, setDatos] = useState({});
  const [esParaMi, setEsParaMi] = useState(true);
  const [avisoAceptado, setAvisoAceptado] = useState(false);
  const [primeraVez, setPrimeraVez] = useState(null); // true = primera vez, false = ya ha venido, null = sin contestar
  const [erroresServidor, setErroresServidor] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [confirmacion, setConfirmacion] = useState(null);

  // Turnstile
  const turnstileToken = useRef(null);
  const widgetContainer = useRef(null);
  const widgetId = useRef(null);

  const hoy = useMemo(() => aFecha(aTexto(new Date())), []);

  // Turnstile: lo decide el backend (su .env), así front y back nunca se desalinean
  const turnstileActivo = !!agenda?.turnstile_activo;

  // 1) Cargar datos del médico
  useEffect(() => {
    const cargar = async () => {
      const resp = await getAgendaPublica(bookingId);
      if (resp.ok) {
        setAgenda(resp.data);
        const inicio = aFecha(resp.data.fecha_min);
        setInicioVentana(inicio);
        setMesVisible(primeroDelMes(inicio));
      } else {
        setErrorGeneral(resp.data?.error || "Esta página no está disponible.");
      }
      setCargando(false);
    };
    cargar();
  }, [bookingId]);

  // Ajustar cuántos días se ven al cambiar el tamaño de la pantalla
  useEffect(() => {
    const alCambiar = () => setDiasPorVista(calcularDiasPorVista());
    window.addEventListener("resize", alCambiar);
    return () => window.removeEventListener("resize", alCambiar);
  }, []);

  const fechaMin = useMemo(() => (agenda ? aFecha(agenda.fecha_min) : null), [agenda]);
  const fechaMax = useMemo(() => (agenda ? aFecha(agenda.fecha_max) : null), [agenda]);

  // ¿Se puede agendar ese día? (dentro del rango y el médico atiende ese día)
  const diaSeleccionable = useCallback(
    (d) =>
      !!agenda &&
      d >= fechaMin &&
      d <= fechaMax &&
      agenda.dias_con_horario.includes(DIAS_JS[d.getDay()]),
    [agenda, fechaMin, fechaMax]
  );

  // Días visibles en la semana
  const ventana = useMemo(() => {
    if (!inicioVentana) return [];
    return Array.from({ length: diasPorVista }, (_, i) => sumarDias(inicioVentana, i));
  }, [inicioVentana, diasPorVista]);

  // ¿Hay al menos un día con horario en todo el rango?
  const hayDias = useMemo(() => {
    if (!agenda) return false;
    for (let d = new Date(fechaMin); d <= fechaMax; d.setDate(d.getDate() + 1)) {
      if (diaSeleccionable(d)) return true;
    }
    return false;
  }, [agenda, fechaMin, fechaMax, diaSeleccionable]);

  // 2) Pedir las horas libres de los días visibles (todos al mismo tiempo)
  useEffect(() => {
    if (!agenda) return;
    const pendientes = ventana
      .filter((d) => diaSeleccionable(d) && horariosPorDia[aTexto(d)] === undefined)
      .map(aTexto);
    if (pendientes.length === 0) return;

    // null = cargando
    setHorariosPorDia((prev) => {
      const nuevo = { ...prev };
      pendientes.forEach((t) => { nuevo[t] = null; });
      return nuevo;
    });

    pendientes.forEach(async (texto) => {
      const resp = await getHorariosPublicos(bookingId, texto);
      setHorariosPorDia((prev) => ({ ...prev, [texto]: resp.ok ? resp.data.horarios : [] }));
    });
  }, [agenda, ventana, diaSeleccionable, horariosPorDia, bookingId]);

  // Turnstile: se dibuja cuando aparece el formulario
  useEffect(() => {
    if (!turnstileActivo || paso !== "datos") return;
    const render = () => {
      if (window.turnstile && widgetContainer.current && widgetId.current === null) {
        widgetId.current = window.turnstile.render(widgetContainer.current, {
          sitekey: TURNSTILE_SITE_KEY,
          appearance: "interaction-only",
          callback: (token) => { turnstileToken.current = token; },
          "error-callback": () => { turnstileToken.current = null; },
          "expired-callback": () => { turnstileToken.current = null; },
        });
      }
    };
    if (window.turnstile) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) { render(); clearInterval(interval); }
      }, 200);
      return () => clearInterval(interval);
    }
    return () => {
      if (window.turnstile && widgetId.current !== null) {
        window.turnstile.remove(widgetId.current);
      }
      widgetId.current = null;
      turnstileToken.current = null;
    };
  }, [paso, turnstileActivo]);

  // ======== Navegación del calendario ========

  const irADia = (d) => {
    setInicioVentana(d);
    setMesVisible(primeroDelMes(d));
  };

  const puedeAtras = !!inicioVentana && inicioVentana > fechaMin;
  const puedeAdelante = !!inicioVentana && sumarDias(inicioVentana, diasPorVista) <= fechaMax;

  const irAtras = () => {
    const nuevo = sumarDias(inicioVentana, -diasPorVista);
    irADia(nuevo < fechaMin ? fechaMin : nuevo);
  };

  const irAdelante = () => irADia(sumarDias(inicioVentana, diasPorVista));

  const puedeMesAtras = !!mesVisible && mesVisible > primeroDelMes(fechaMin);
  const puedeMesAdelante = !!mesVisible && mesVisible < primeroDelMes(fechaMax);

  const cambiarMes = (n) =>
    setMesVisible((prev) => new Date(prev.getFullYear(), prev.getMonth() + n, 1));

  

  // Celdas del calendario chiquito (null = hueco antes del día 1)
  const celdasMes = useMemo(() => {
    if (!mesVisible) return [];
    const anio = mesVisible.getFullYear();
    const mes = mesVisible.getMonth();
    const totalDias = new Date(anio, mes + 1, 0).getDate();
    const huecos = Array(mesVisible.getDay()).fill(null);
    const dias = Array.from({ length: totalDias }, (_, i) => new Date(anio, mes, i + 1));
    return [...huecos, ...dias];
  }, [mesVisible]);

  // ======== Acciones ========

  const elegirHora = (texto, hora) => {
    setFechaElegida(texto);
    setHoraElegida(hora);
    setAvisoHora("");
    setPaso("datos");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAgendar = async (e) => {
    e.preventDefault();
    setErroresServidor({});

    if (primeraVez === null) {
      toast.error("Indica si es la primera consulta con el médico.");
      return;
    }

    const campos = agenda.campos;
    if (!validarDatosPaciente({ campos, datos, esParaMi, avisoAceptado, requiereContacto: true })) return;

    if (turnstileActivo && !turnstileToken.current) {
      toast.error("Por favor completa la verificación de seguridad.");
      return;
    }

    const payload = {
      ...armarPayloadPaciente({ campos, datos, esParaMi }),
      fecha: fechaElegida,
      hora: horaElegida,
      primera_vez: primeraVez,
      ...(turnstileActivo ? { cf_turnstile_response: turnstileToken.current } : {}),
    };

    setEnviando(true);
    const resp = await agendarPublico(bookingId, payload);
    setEnviando(false);

    if (resp.ok) {
      setConfirmacion(resp.data);
      setPaso("listo");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Alguien más ganó la hora: regresar a elegir, conservando sus datos,
    // y volver a pedir las horas libres de ese día
    if (resp.data?.motivo === "hora_ocupada") {
      setAvisoHora(resp.data.error);
      setHorariosPorDia((prev) => {
        const nuevo = { ...prev };
        delete nuevo[fechaElegida];
        return nuevo;
      });
      setPaso("fecha");
      return;
    }

    if (resp.data?.motivo) {
      // Página apagada, fecha fuera de rango, seguridad...
      if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
      toast.error(resp.data.error);
      return;
    }

    // Errores por campo del backend
    setErroresServidor(resp.data || {});
  };

  // ======== Piezas comunes ========

   const marca = (
    <a href="/home" className="ap-marca" aria-label="Salvir">
      <img src={logoSalvir} alt="" className="ap-marca-icono" />
      <img src={logoSalvirLetras} alt="Salvir" className="ap-marca-letras" />
    </a>
  );

  const pie = (
    <footer className="ap-pie">
      <div className="ap-pie-caja">
        <img src={logoSalvir} alt="" className="ap-pie-icono" />
        <span>¿Eres profesional de la salud? Crea tu propia página de citas con Salvir.</span>
        <a href="/home">Más información</a>
      </div>
      <p className="ap-pie-legal">
        Con la tecnología de <a href="/home">Salvir</a>.<br />
      </p>
    </footer>
  );

  // ======== Pantallas ========

  if (cargando) {
    return (
      <div className="ap-pagina">
        <div className="ap-tarjeta ap-centro"><p className="ap-texto-suave">Cargando…</p></div>
      </div>
    );
  }

  if (errorGeneral) {
    return (
      <div className="ap-pagina">
        <div className="ap-tarjeta ap-centro">
          <div className="ap-marca-centro">{marca}</div>
          <p className="ap-texto-suave">{errorGeneral}</p>
        </div>
        {pie}
      </div>
    );
  }

  const { medico } = agenda;

  const encabezado = (
    <header className="ap-encabezado">
      <div className="ap-medico">
        <div className="ap-avatar">{iniciales(medico.nombre)}</div>
        <div>
          <h1 className="ap-medico-nombre">{medico.nombre}</h1>
          {medico.especialidad && <p className="ap-medico-especialidad">{medico.especialidad}</p>}
          {medico.clinica && <p className="ap-medico-clinica">{medico.clinica}</p>}
        </div>
      </div>

      <div className="ap-info">
        <h2 className="ap-info-titulo">Agenda tu cita</h2>
        <p className="ap-info-dato">
          <span className="ap-icono">⏱</span>Citas de {agenda.duracion_minutos} min
        </p>
        {medico.direccion && (
          <p className="ap-info-dato">
            <span className="ap-icono">📍</span>{medico.direccion}
          </p>
        )}
      </div>

      {marca}
    </header>
  );

  // Paso final
  if (paso === "listo" && confirmacion) {
    return (
      <div className="ap-pagina">
        {encabezado}
        <section className="ap-tarjeta ap-listo">
          <div className="ap-listo-icono">✓</div>
          <h3 className="ap-listo-titulo">{confirmacion.mensaje}</h3>
          <div className="ap-resumen ap-resumen-listo">
            <p className="ap-resumen-fecha">{formatoLargo(confirmacion.cita.fecha)}</p>
            <p className="ap-resumen-hora">
              {formatoHora(confirmacion.cita.hora)} · {confirmacion.cita.duracion_minutos} min
            </p>
            {confirmacion.medico?.direccion && (
              <p className="ap-resumen-dato">📍 {confirmacion.medico.direccion}</p>
            )}
          </div>
          <p className="ap-texto-suave">
            El consultorio podría contactarte por WhatsApp para confirmar tu cita.
          </p>
        </section>
        {pie}
      </div>
    );
  }

  return (
    <div className="ap-pagina">
      {encabezado}

      {/* Paso 1: día y hora */}
      {paso === "fecha" && (
        <section className="ap-tarjeta">
          <div className="ap-tarjeta-cabecera">
            <h3 className="ap-tarjeta-titulo">Selecciona una hora para tu cita</h3>
            <span className="ap-zona">Hora de Ciudad de México</span>
          </div>

          {avisoHora && <p className="ap-aviso">{avisoHora}</p>}

          {!hayDias ? (
            <p className="ap-texto-suave">Por ahora no hay días disponibles para agendar.</p>
          ) : (
            <div className="ap-selector">
              {/* Calendario chiquito del mes */}
              <aside className="ap-mini-cal">
                <div className="ap-mini-cal-cabecera">
                  <span className="ap-mini-cal-mes">
                    {MESES[mesVisible.getMonth()]} de {mesVisible.getFullYear()}
                  </span>
                  <div className="ap-mini-cal-flechas">
                    <button
                      type="button"
                      className="ap-mini-flecha"
                      disabled={!puedeMesAtras}
                      onClick={() => cambiarMes(-1)}
                      aria-label="Mes anterior"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="ap-mini-flecha"
                      disabled={!puedeMesAdelante}
                      onClick={() => cambiarMes(1)}
                      aria-label="Mes siguiente"
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="ap-mini-cal-grid">
                  {LETRAS_SEMANA.map((letra, i) => (
                    <span key={`letra-${i}`} className="ap-mini-letra">{letra}</span>
                  ))}
                  {celdasMes.map((d, i) =>
                    d === null ? (
                      <span key={`hueco-${i}`} />
                    ) : (
                      <button
                        type="button"
                        key={aTexto(d)}
                        className={[
                          "ap-mini-dia",
                          diaSeleccionable(d) ? "disponible" : "deshabilitado",
                          inicioVentana && mismoDia(d, inicioVentana) ? "seleccionado" : "",
                          mismoDia(d, hoy) ? "hoy" : "",
                        ].join(" ")}
                        disabled={!diaSeleccionable(d)}
                        onClick={() => irADia(d)}
                      >
                        {d.getDate()}
                      </button>
                    )
                  )}
                </div>
              </aside>

              {/* Semana con horas libres */}
              <div className="ap-semana">
                <button
                  type="button"
                  className="ap-flecha"
                  disabled={!puedeAtras}
                  onClick={irAtras}
                  aria-label="Días anteriores"
                >
                  ‹
                </button>

                <div
                  className="ap-columnas"
                  style={{ gridTemplateColumns: `repeat(${diasPorVista}, minmax(0, 1fr))` }}
                >
                  {ventana.map((d) => {
                    const texto = aTexto(d);
                    const habil = diaSeleccionable(d);
                    const lista = horariosPorDia[texto];

                    return (
                      <div key={texto} className="ap-columna">
                        <div className={`ap-dia-cabecera ${mismoDia(d, hoy) ? "hoy" : ""}`}>
                          <span className="ap-dia-nombre">{DIAS_CORTOS[d.getDay()]}</span>
                          <span className="ap-dia-numero">{d.getDate()}</span>
                        </div>

                        <div className="ap-horas">
                          {!habil ? (
                            <span className="ap-sin-horario">—</span>
                          ) : lista === undefined || lista === null ? (
                            <span className="ap-cargando">…</span>
                          ) : lista.length === 0 ? (
                            <span className="ap-sin-horario">—</span>
                          ) : (
                            lista.map((hora) => (
                              <button
                                type="button"
                                key={hora}
                                className="ap-hora"
                                onClick={() => elegirHora(texto, hora)}
                              >
                                {formatoHora(hora)}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="ap-flecha"
                  disabled={!puedeAdelante}
                  onClick={irAdelante}
                  aria-label="Días siguientes"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Paso 2: datos */}
      {paso === "datos" && (
        <section className="ap-tarjeta ap-datos">
          <div className="ap-resumen">
            <p className="ap-resumen-etiqueta">Tu cita</p>
            <p className="ap-resumen-fecha">{formatoLargo(fechaElegida)}</p>
            <p className="ap-resumen-hora">
              {formatoHora(horaElegida)} · {agenda.duracion_minutos} min
            </p>
            {medico.direccion && <p className="ap-resumen-dato">📍 {medico.direccion}</p>}
            <button type="button" className="ap-boton-secundario" onClick={() => setPaso("fecha")}>
              Cambiar día u hora
            </button>
          </div>

          <form onSubmit={handleAgendar} className="ap-formulario">
            <h3 className="ap-tarjeta-titulo">Tus datos</h3>

            
            <label className="ficha-campo">
              <span>¿Es la primera consulta con {medico.nombre}? *</span>
              <select
                value={primeraVez === null ? "" : primeraVez ? "si" : "no"}
                onChange={(e) =>
                  setPrimeraVez(e.target.value === "" ? null : e.target.value === "si")
                }
              >
                <option value="">Selecciona…</option>
                <option value="si">Sí, es la primera vez</option>
                <option value="no">No, ya ha consultado antes</option>
              </select>
            </label>

            <FormularioDatosPaciente
              campos={agenda.campos}
              datos={datos}
              setDatos={setDatos}
              esParaMi={esParaMi}
              setEsParaMi={setEsParaMi}
              avisoTexto={agenda.aviso_privacidad?.texto}
              avisoUrl={`/aviso-privacidad/agenda/${bookingId}`}
              avisoAceptado={avisoAceptado}
              setAvisoAceptado={setAvisoAceptado}
              erroresServidor={erroresServidor}
            />
            

            {/* Widget de Turnstile */}
            <div ref={widgetContainer}></div>

            <button type="submit" className="ficha-boton ap-boton-agendar" disabled={enviando}>
              {enviando ? "Agendando…" : "Agendar cita"}
            </button>
          </form>
        </section>
      )}

      {pie}
    </div>
  );
};

export default AgendaPublica;